<script setup lang="ts">
const { t } = useI18n()

useHead({
  titleTemplate: () => `${t("chats.title[0]")} ${t("chats.title[1]")}`,
  meta: [
    { name: "description", content: () => `${t("chats.subtitle[0][0]")} ${t("chats.subtitle[0][1]")}${t("chats.subtitle[0][2]")}` },
    { property: "og:title", content: () => `${t("chats.title[0]")} ${t("chats.title[1]")}` },
    { property: "og:description", content: () => `${t("chats.subtitle[0][0]")} ${t("chats.subtitle[0][1]")}${t("chats.subtitle[0][2]")}` },
    { property: "og:image", content: "https://smsbat.com/images/chat/mobile-1.png" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})

const popularQuestions = ref([
  {
    question: t('chats.questions[0].question'),
    answer: t('chats.questions[0].answer'),
    isOpen: false
  },
  {
    question: t('chats.questions[1].question'),
    answer: t('chats.questions[1].answer'),
    isOpen: false
  },
  {
    question: t('chats.questions[2].question'),
    answer: t('chats.questions[2].answer'),
    isOpen: false
  },
])
</script>

<template>
  <div>
    <h1 class="page-title"><strong>{{ $t('chats.title[0]') }}</strong></h1>
    <div class="page-title !my-0">{{ $t('chats.title[1]') }}</div>
    <div class="page-title !my-0">{{ $t('chats.title[2]') }}</div>

    <!-- App Download Promos -->
    <div class="flex justify-center items-center gap-4 mt-8 flex-wrap">
      <!-- Google Play -->
      <a href="https://play.google.com/store/apps/details?id=com.smsbat.chat" target="_blank" rel="noopener noreferrer" class="transition-transform hover:-translate-y-1 inline-block">
        <NuxtImg src="/images/google-play-badge-en.svg" width="180" height="53" alt="Get it on Google Play" class="h-[40px] md:h-[50px] w-auto drop-shadow-sm" />
      </a>
      
      <!-- App Store (Coming Soon) -->
      <div class="relative inline-block transition-transform hover:-translate-y-1 cursor-default group focus:outline-none">
        <NuxtImg src="/images/app-store-badge.svg" width="120" height="40" alt="Download on the App Store" class="h-[40px] md:h-[50px] w-auto drop-shadow-sm transition-all duration-300" />
        <div class="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-blue-600 text-white text-[10px] md:text-sm font-bold px-2 md:px-3 py-1 rounded-full shadow-lg z-10 whitespace-nowrap border-2 border-white pointer-events-none transition-all duration-300 group-hover:scale-110">
          {{ $i18n.locale === 'uk-ua' ? 'Скоро' : 'Coming Soon' }}
        </div>
      </div>
    </div>

    <!-- App Showcases -->
    <div class="page-section pt-16 pb-20 mt-12 overflow-hidden relative">
      <div class="absolute inset-0 bg-blue-50/50 skew-y-1 origin-top-left -z-10"></div>
      <div class="container mx-auto px-4 max-w-7xl">
        <!-- Mobile Mockups -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 md:mb-24">
           <div class="flex justify-center group perspective-1000">
             <NuxtImg src="/images/chat/mobile-1.png" width="571" height="1024" format="webp" loading="eager" fetchpriority="high" alt="All Customers in One Window" class="w-full max-w-[320px] h-auto rounded-[2rem] shadow-2xl transition-all duration-700 ease-out hover:-translate-y-4 hover:rotate-y-6" />
           </div>
           <div class="flex justify-center group perspective-1000">
             <NuxtImg src="/images/chat/mobile-2.png" width="571" height="1024" format="webp" loading="eager" fetchpriority="high" alt="Smart Automation" class="w-full max-w-[320px] h-auto rounded-[2rem] shadow-2xl transition-all duration-700 ease-out md:translate-y-12 hover:-translate-y-2 hover:-rotate-y-6" />
           </div>
           <div class="flex justify-center group perspective-1000">
             <NuxtImg src="/images/chat/mobile-3.png" width="571" height="1024" format="webp" loading="eager" fetchpriority="high" alt="Complete Customer Profile" class="w-full max-w-[320px] h-auto rounded-[2rem] shadow-2xl transition-all duration-700 ease-out md:translate-y-24 hover:translate-y-20 hover:rotate-y-6" />
           </div>
        </div>

        <!-- Tablet Mockups -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-20 max-w-6xl mx-auto">
           <div class="flex justify-center group">
             <NuxtImg src="/images/chat/tablet-1.png" width="1024" height="571" format="webp" loading="lazy" alt="Maximize Your Support Workflow" class="w-full h-auto rounded-3xl shadow-2xl transition-all duration-700 hover:scale-[1.03]" />
           </div>
           <div class="flex justify-center group">
             <NuxtImg src="/images/chat/tablet-2.png" width="1024" height="571" format="webp" loading="lazy" alt="AI-Powered Efficiency" class="w-full h-auto rounded-3xl shadow-2xl transition-all duration-700 hover:scale-[1.03] lg:translate-y-12" />
           </div>
        </div>
      </div>
    </div>
    <div class="page-section">
      <div class="container">
        <div class="section-content">
          <div class="service-hint">
            <div class="section-content">
              <p>{{ $t('chats.subtitle[0][0]') }} <span class="highlight-blue">{{ $t('chats.subtitle[0][1]') }}</span>{{
                $t('chats.subtitle[0][2]') }}</p>
              <p>{{ $t('chats.subtitle[1][0]') }}<span class="highlight-blue">{{ $t('chats.subtitle[1][1]') }}</span>.
              </p>
              <p class="page-sub">{{ $t('chats.subtitle[2][0]') }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="page-section service-types">
        <div class="container">
          <div class="mail-bg-img"></div>
          <div class="phone-bg-img"></div>
          <div class="types-content">
            <div class="types-img">
              <NuxtImg src="/images/service-type.png" width="80" height="80" format="webp" loading="lazy" alt="Service Image" />
            </div>
            <div class="service-list">
              <div class="service-item">{{ $t('chats.servicesList[0]') }}</div>
              <div class="service-item">{{ $t('chats.servicesList[1]') }}</div>
              <div class="service-item">{{ $t('chats.servicesList[2]') }}</div>
            </div>
            <div class="service-list">
              <div class="service-item">{{ $t('chats.servicesList[3]') }}</div>
              <div class="service-item">{{ $t('chats.servicesList[4]') }}</div>
              <div class="service-item">{{ $t('chats.servicesList[5]') }}</div>
            </div>
          </div>
        </div>
      </div>
      <div class="page-section service-advantages">
        <div class="container">
          <div class="section-content">
            <div class="advantages-title">{{ $t('chats.advantages.title') }}</div>
            <div class="advantages-list">
              <div class="advantage-item">
                <div class="item-count">01</div>
                <div class="item-title break-all md:break-normal">{{ $t('chats.advantages.list[0][0]') }}</div>
                <div class="item-text break-all md:break-normal">{{ $t('chats.advantages.list[0][1]') }}</div>
              </div>
              <div class="advantage-item">
                <div class="item-count">02</div>
                <div class="item-title break-all md:break-normal">{{ $t('chats.advantages.list[1][0]') }}</div>
                <div class="item-text break-all md:break-normal">{{ $t('chats.advantages.list[1][1]') }}</div>
              </div>
              <div class="advantage-item">
                <div class="item-count">03</div>
                <div class="item-title break-all md:break-normal">{{ $t('chats.advantages.list[2][0]') }}</div>
                <div class="item-text break-all md:break-normal">{{ $t('chats.advantages.list[2][1]') }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="page-section">
        <div class="container">
          <div class="section-content">
            <div class="service-hint">
              <p>{{ $t('chats.unlockPower[0]') }} <span class="highlight-blue">{{ $t('chats.unlockPower[1]') }}</span>{{
                $t('chats.unlockPower[2]') }}</p>
              <p>{{ $t('chats.unlockPower[3]') }}<span class="highlight-blue">{{ $t('chats.unlockPower[4]') }}</span>
                {{ $t('chats.unlockPower[5]') }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="page-page-section price-section service-domain">
        <div class="container">
          <div class="service-price">
            <div class="price-label">{{ $t('global.price') }}</div>
          </div>
          <div class="section-head">
            <div class="section-head-title">{{ $t('chats.cost.title') }}</div>
          </div>
          <div class="section-content">
            <div class="price-list-wrapper">
              <div class="price-list">
                <div class="price-item">
                  <div class="price-item-label">{{ $t('chats.cost.list[0][0]') }}</div>
                  <div class="price-item-value">{{ $t('chats.cost.list[0][1]') }}<span
                      style="font-size:13px">&nbsp;/&nbsp;{{ $t('chats.cost.list[0][2]') }}</span></div>
                </div>
                <div class="price-item">
                  <div class="price-item-label">{{ $t('chats.cost.list[1][0]') }}</div>
                  <div class="price-item-value">{{ $t('chats.cost.list[1][1]') }}<span
                      style="font-size:13px">&nbsp;/&nbsp;{{ $t('chats.cost.list[1][2]') }}</span></div>
                </div>
                <div class="price-item">
                  <div class="price-item-label">{{ $t('chats.cost.list[2][0]') }}</div>
                  <div class="price-item-value whitespace-nowrap !text-sm flex flex-col md:flex-row">
                    <span>{{ $t('chats.cost.list[2][1]') }}</span>
                    <span style="font-size:13px" class="whitespace-normal text-center mx-2">{{
                      $t('chats.cost.list[2][2]') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="page-section">
        <div class="container">
          <PopularQuestions :title="$t('chats.questionsTitle')" :questions="popularQuestions" />
        </div>
      </div>
      <ContactForm :indexbat-form-id="74" />
    </div>
  </div>
</template>